import React, { FunctionComponent, ReactNode } from "react"
import { RunDto } from "@custom-types/modelRun"
import { useGetGraphDataQuery } from "@store/services/graphsService"
import { RunModelDto, useGetModelQuery } from "@store/services/modelsService"
import CircularProgress from "@mui/material/CircularProgress"
import { GraphNodeData, GroupNodeData } from "@store/services/converter/types"
import Paper from "@mui/material/Paper"
import TableContainer from "@mui/material/TableContainer"
import TableCell from "@mui/material/TableCell"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import {
  GraphDiffData,
  GraphDiffType,
  calculateGraphDiff,
} from "@features/graph/utils/graphNodeUtils"

interface Props {
  data1?: RunDto
  data2?: RunDto
}

interface DiffProps {
  data1?: RunModelDto
  data2?: RunModelDto
}

interface DiffRowProps {
  diff: GraphDiffData
}

const TableCellCenter = (props: { children: ReactNode }) => (
  <TableCell align="center">{props.children}</TableCell>
)

export const GraphModelDiffTable: FunctionComponent<Props> = (props) => {
  if (!props.data1 || !props.data2) return <ErrorMissingData />
  // eslint-disable-next-line react-hooks/rules-of-hooks -- FIX: hooks that can't be called conditionally
  const { data: graphModel1 } = useGetModelQuery(props.data1.model_key)
  // eslint-disable-next-line react-hooks/rules-of-hooks -- FIX: hooks that can't be called conditionally
  const { data: graphModel2 } = useGetModelQuery(props.data2.model_key)
  if (graphModel1 && graphModel2) {
    return <GraphDiffTable data1={graphModel1} data2={graphModel2} />
  }
  return <CircularProgress />
}

const GraphDiffTable: FunctionComponent<DiffProps> = (props) => {
  if (!props.data1 || !props.data2) return <CircularProgress />
  // eslint-disable-next-line react-hooks/rules-of-hooks -- FIX: hooks that can't be called conditionally
  const { data: graphData1 } = useGetGraphDataQuery(props.data1.graph_key)
  // eslint-disable-next-line react-hooks/rules-of-hooks -- FIX: hooks that can't be called conditionally
  const { data: graphData2 } = useGetGraphDataQuery(props.data2.graph_key)
  if (!graphData1 || !graphData2) return <CircularProgress />
  const diff = calculateGraphDiff(graphData1, graphData2)
  if (diff.length === 0) return <SameGraphMessage />
  return (
    <div>
      <h1>Graph Difference</h1>
      <TableContainer
        component={Paper}
        sx={{ overflowX: "hidden", padding: 0, backgroundColor: "#FAF9F6" }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCellCenter>
                <b>Node</b>
              </TableCellCenter>
              <TableCellCenter>
                <b>{props.data1.graph_key}</b>
              </TableCellCenter>
              <TableCellCenter>
                <b>{props.data2.graph_key}</b>
              </TableCellCenter>
            </TableRow>
          </TableHead>
          <TableBody>
            {diff.map((row, index) => (
              <RowDiffRow diff={row} key={index} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  )
}

const RowDiffRow: FunctionComponent<DiffRowProps> = ({ diff }) => {
  let label = ""
  let n1Message = ""
  let n2Message = ""
  switch (diff.type) {
    case GraphDiffType.NodeAdded: {
      label = (diff.n1!.data as GraphNodeData).display.label
      n1Message = "Added"
      break
    }
    case GraphDiffType.NodeMissing: {
      label = (diff.n2!.data as GraphNodeData).display.label
      n2Message = "Missing"
      break
    }
    case GraphDiffType.Expression: {
      label = (diff.n1!.data as GraphNodeData).display.label
      n1Message = (diff.n1!.data as GraphNodeData).expression || ""
      n2Message = (diff.n2!.data as GraphNodeData).expression || ""
      break
    }
    case GraphDiffType.NodeLabel: {
      label = (diff.n1!.data as GraphNodeData).display.label
      n1Message = (diff.n1!.data as GraphNodeData).display.label
      n2Message = (diff.n2!.data as GraphNodeData).display.label
      break
    }
    case GraphDiffType.GroupLabel: {
      label = (diff.n1!.data as GroupNodeData).group.label
      n1Message = (diff.n1!.data as GroupNodeData).group.label
      n2Message = (diff.n2!.data as GroupNodeData).group.label
      break
    }
  }
  return (
    <TableRow>
      <TableCellCenter>{label}</TableCellCenter>
      <TableCellCenter>{n1Message}</TableCellCenter>
      <TableCellCenter>{n2Message}</TableCellCenter>
    </TableRow>
  )
}

const ErrorMissingData: FunctionComponent = () => {
  return (
    <div>
      <h1>Graph Difference</h1>
      <h3>No 2 graphs to compare data was provided</h3>
    </div>
  )
}

const SameGraphMessage: FunctionComponent = () => {
  return (
    <div>
      <h1>Graph Difference</h1>
      <h3>Graphs are the same</h3>
    </div>
  )
}
